/*  portfolio_requests_progress_helpers.ts
    – Pure helpers behind the "Portfolio requests performance" home tab
      (routes/dashboard/portfolio_requests_progress.tsx): stage classification,
      per-currency amounts and formatting. No React or firebase imports so they
      can be unit tested.
*/
import { Service, ServiceStatusCode } from "../../types";

export const TREND_MONTHS = 12;
export const MANAGEMENT_LABELS: Record<string, string> = { dpm: "DPM", "non-dpm": "Non-DPM", custody: "Custody" };

export type Stage = "in_progress" | "executed" | "rejected";
export type Period = "all" | "this_month" | "last_3_months" | "ytd" | "last_12_months";
export type AmountByCurrency = Record<string, number>;
export type Bucket = { requests: number; clients: Set<string>; amount: AmountByCurrency };
export type SegmentRow = {
    segment: string;
    requests: number;
    clients: number;
    inProgress: number;
    executed: number;
    rejected: number;
    amount: AmountByCurrency;
    paidAmount: AmountByCurrency;
};
export type CountRow = { label: string; requests: number; clients: Set<string> };
/** One row of the "paid vs not paid" pivots: a stage or a segment split by whether the transfer was received. */
export type PaidSplitRow = { label: string; notPaid: Bucket; paid: Bucket; total: Bucket };
export const newBucket = (): Bucket => ({ requests: 0, clients: new Set(), amount: {} });
export const newPaidSplitRow = (label: string): PaidSplitRow => ({ label, notPaid: newBucket(), paid: newBucket(), total: newBucket() });
export type PieDatum = { id: number; value: number; label: string };


export const periodStart = (period: Period): Date | undefined => {
    const now = new Date();
    switch (period) {
        case "this_month": return new Date(now.getFullYear(), now.getMonth(), 1);
        case "last_3_months": return new Date(now.getFullYear(), now.getMonth() - 2, 1);
        case "ytd": return new Date(now.getFullYear(), 0, 1);
        case "last_12_months": return new Date(now.getFullYear(), now.getMonth() - (TREND_MONTHS - 1), 1);
        default: return undefined;
    }
};

/** Firestore Timestamps, {seconds} maps, Dates and ISO strings all show up in service docs. */
export const toDate = (value: unknown): Date | undefined => {
    if (!value) return undefined;
    if (value instanceof Date) return isNaN(value.getTime()) ? undefined : value;
    const withToDate = value as { toDate?: () => Date; seconds?: number };
    if (typeof withToDate.toDate === "function") return toDate(withToDate.toDate());
    if (typeof withToDate.seconds === "number") return new Date(withToDate.seconds * 1000);
    if (typeof value === "string" || typeof value === "number") return toDate(new Date(value));
    return undefined;
};

export const requestDate = (service: Service) => toDate(service.submittedAt) ?? toDate(service.created);
/** When Geneva confirmed the portfolio; undefined until then. */
export const executionDate = (service: Service) => toDate(service.geneva_responses?.create_portfolio?.timestamp);

export const normalizeNumber = (value: unknown) => {
    const normalized = typeof value === "string" ? value.replace(/,/g, "") : value;
    const numeric = typeof normalized === "number" ? normalized : Number(normalized);
    return Number.isFinite(numeric) ? numeric : 0;
};

/** Investment amount per currency: the invoices without fees, else the amount typed on the opening request. */
export const requestAmounts = (service: Service): AmountByCurrency => {
    const amounts: AmountByCurrency = {};
    const invoices = service.transferInvoices ?? [];
    if (invoices.length) {
        invoices.forEach((invoice) => {
            const amount = normalizeNumber(invoice.initialAmount ?? invoice.amount);
            const currency = invoice.currency || service.portfolioOpeningRequest?.currency || "KWD";
            if (amount) amounts[currency] = (amounts[currency] || 0) + amount;
        });
        return amounts;
    }
    const amount = normalizeNumber(service.portfolioOpeningRequest?.transferAmount);
    if (amount) amounts[service.portfolioOpeningRequest?.currency || "KWD"] = amount;
    return amounts;
};

export const addAmounts = (target: AmountByCurrency, source: AmountByCurrency) => {
    Object.entries(source).forEach(([currency, amount]) => {
        target[currency] = (target[currency] || 0) + amount;
    });
};

export const scaleValue = (value: number) => {
    const abs = Math.abs(value);
    if (abs >= 1_000_000_000) return { scaled: value / 1_000_000_000, unit: "B" };
    if (abs >= 1_000_000) return { scaled: value / 1_000_000, unit: "M" };
    if (abs >= 1_000) return { scaled: value / 1_000, unit: "K" };
    return { scaled: value, unit: "" };
};

export const formatAmount = (value: number, currency?: string) => {
    if (!Number.isFinite(value)) return "--";
    const { scaled, unit } = scaleValue(value);
    const formatter = new Intl.NumberFormat("en-US", { minimumFractionDigits: 3, maximumFractionDigits: 3 });
    return `${formatter.format(scaled)}${unit}${currency ? ` ${currency}` : ""}`;
};

/** "1.100M USD · 250.000K KWD"; "--" when nothing was requested. */
export const formatAmounts = (amounts: AmountByCurrency) => {
    const entries = Object.entries(amounts).filter(([, amount]) => amount).sort(([a], [b]) => a.localeCompare(b));
    return entries.length ? entries.map(([currency, amount]) => formatAmount(amount, currency)).join(" · ") : "--";
};

export const formatCount = (value: number) => new Intl.NumberFormat("en-US").format(value);

export const getSegment = (service: Service) =>
    service.referredBy?.segment ||
    service.rm?.segment ||
    service.submittedBy?.segment ||
    service.segment ||
    service.customerObj?.segment ||
    "Unspecified";

export const getClientId = (service: Service) =>
    service.customerObj?.id ||
    service.customerObj?.civilID ||
    service.customerObj?.mobile ||
    service.id ||
    "unknown";

export const getStage = (service: Service): Stage => {
    const status = service.tags?.serviceStatus?.code as ServiceStatusCode | undefined;
    if (status === ServiceStatusCode.rejected || status === ServiceStatusCode.failed) return "rejected";
    if (status === ServiceStatusCode.done) return "executed";
    return "in_progress";
};

export const getPortfolioFallbackName = (portfolioDbId: string) =>
    portfolioDbId
        .replace(/[_-]+/g, " ")
        .replace(/\b\w/g, (character) => character.toUpperCase())
        .replace(/\bDpm\b/g, "DPM");

export const monthKey = (date: Date) => `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}`;
